import { useEffect } from 'react';
import { useSessionStore } from './store/sessionStore';
import { initSession, leaveRoom } from './session/sessionRuntime';
import { StatusBar } from './components/StatusBar';
import { LobbyView } from './views/LobbyView';
import { RoleRevealView } from './views/RoleRevealView';
import { WordPickView } from './views/WordPickView';
import { PlayingView } from './views/PlayingView';
import { EndView } from './views/EndView';
import { RoomEntryView } from './views/RoomEntryView';
import { useRoute, navigate, parsePath } from './hooks/useRoute';
import { getLastRoom } from './protocol/roomStorage';

export default function App(): JSX.Element {
  const route = useRoute();

  // 入口页：如果 localStorage 还存着 lastRoom，自动跳回去（PRD §B.3）
  useEffect(() => {
    if (route.name !== 'entry') return;
    if (typeof window === 'undefined') return;
    // 防止跳回后又被这里再次跳走
    if (parsePath(window.location.pathname).name !== 'entry') return;
    const last = getLastRoom();
    if (last) navigate(`/r/${last}`);
  }, [route.name]);

  if (route.name === 'entry') {
    return <RoomEntryView />;
  }
  return <RoomView roomCode={route.roomCode} />;
}

function RoomView({ roomCode }: { roomCode: string }): JSX.Element {
  const phase = useSessionStore((s) => s.phase);
  const currentRoom = useSessionStore((s) => s.roomCode);

  useEffect(() => {
    initSession(roomCode);
  }, [roomCode]);

  return (
    <div className="mx-auto min-h-full max-w-md bg-pig-50">
      <StatusBar />
      <div className="flex items-center justify-between px-4 py-2 text-xs text-pig-600">
        <span data-testid="room-code-display">房间：{currentRoom ?? roomCode}</span>
        <button
          type="button"
          data-testid="btn-leave-room"
          onClick={() => leaveRoom()}
          className="rounded border border-pig-300 px-2 py-1 text-pig-600 hover:bg-pig-100"
        >
          退出房间
        </button>
      </div>
      <PhaseView phase={phase} />
    </div>
  );
}

function PhaseView({ phase }: { phase: string }): JSX.Element {
  switch (phase) {
    case 'lobby':
      return <LobbyView />;
    case 'role_reveal':
      return <RoleRevealView />;
    case 'word_pick':
      return <WordPickView />;
    case 'playing':
      return <PlayingView />;
    case 'end':
      return <EndView />;
    default:
      return <LobbyView />;
  }
}
